'use client'

import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import { useProductStock } from 'product/services/productStock/client'
import { AddToBagButton } from 'product-card/components/shared/ProductSizes/AddToBag/AddToBagButton'
import { dataTestIds } from 'product-card/constants/dataTestIds'
import { useProductCard } from 'product-card/hooks/useProductCard'
import { useProductCardInteractionContext } from 'product-card/hooks/useProductCardInteractionContext/useProductCardInteractionContext'
import { useProductCardStock } from 'product-card/hooks/useProductCardStock'
import { useSelectedColor } from 'product-card/hooks/useSelectedColor/useSelectedColor'
import { useMemo } from 'react'
import { cx } from 'utils/cx'
import styles from './SizePicker.module.scss'

interface SizePickerProps {
	readonly isVisible?: boolean
}
export const SizePicker = ({ isVisible = false }: SizePickerProps) => {
	const { product, productId, productColors, selectedColorId, dataTestId } =
		useProductCard()

	const { setSizePickerVisibilityToggle } = useProductCardInteractionContext()

	const {
		country: { countryISO },
	} = useMasterData()

	const { selectedColor } = useSelectedColor()

	// Filter related products to show only the sizes of the selected color
	// that are not related to other products
	const initialProductSizes = useMemo(
		() => selectedColor?.sizes.filter((size) => !size.relatedProductId),
		[selectedColor]
	)

	const { data: productStock, isLoading: isProductStockLoading } =
		useProductStock({
			productId,
			country: countryISO,
			enabled: isVisible,
		})

	const { productSizesFullInfo } = useProductCardStock({
		productStock,
		productColors,
	})

	const isLoadingOrNoSizes =
		!productSizesFullInfo ||
		productSizesFullInfo.length === 0 ||
		isProductStockLoading

	if (!initialProductSizes && isLoadingOrNoSizes) {
		return null
	}

	const handleOnAddedToBag = () => {
		setSizePickerVisibilityToggle(false)
	}

	return (
		<div
			className={styles.sizePicker}
			data-testid={dataTestIds.productCardSizePanel(dataTestId)}
		>
			<div
				data-testid={dataTestIds.productCardSizePickerContent}
				className={styles.sizePickerContent}
			>
				{isLoadingOrNoSizes
					? initialProductSizes?.map((size) => (
							<AddToBagButton
								key={`${size.id}/${selectedColorId}/${productId}`}
								className={cx(styles.sizePickerAddToBagButton)}
								product={product}
								size={size}
								onClose={handleOnAddedToBag}
							/>
						))
					: productSizesFullInfo.map((size) => (
							<AddToBagButton
								key={`${size.id}/${selectedColorId}/${productId}`}
								className={styles.sizePickerAddToBagButton}
								product={product}
								size={size}
								onClose={handleOnAddedToBag}
							/>
						))}
			</div>
		</div>
	)
}
